import { useState, useContext, useCallback } from "react";
import {
  Typography,
  Tabs,
  Collapse,
  Form,
  Button,
  Row,
  Col,
  Divider,
} from "antd";
import { QuestionCircleTwoTone } from "@ant-design/icons";

import { useMappingFieldsContext } from "../../../contexts/MappingFields";
import { SpecSettingContext } from "../../../pages/SpecSetting";
import FieldMappingRow from "./FieldMappingRow";
import FieldMappingStepDrawer from "./FieldMappingStepDrawer";

const { Title, Text } = Typography;

const FieldMappingStep = ({ approval }) => {
  const { blueprint, updateSettingBlueprint } = useContext(SpecSettingContext);
  const connectionType = blueprint["connectionConfig"]["type"];
  const {
    workOrderRequiredFields,
    workOrderOptionalFields,
    productRequiredFields,
    productOptionalFields,
    workOrderOpHistoryRequiredFields,
    workOrderOpHistoryOptionalFields,
    routeRequiredFields,
    routeOptionalFields,
    opRequiredFields,
    opOptionalFields,
    equipmentRequiredFields,
    workerRequiredFields,
    workerOptionalFields,
  } = useMappingFieldsContext();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [joinForm] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [transformForm] = Form.useForm();

  const updateFieldMapping = useCallback(
    (fieldPath, mapping) => {
      const newFieldMapping = { ...blueprint["fieldMapping"] };
      let target = newFieldMapping;

      const pathParts = fieldPath.split(".");
      for (let i = 0; i < pathParts.length - 1; i++) {
        if (!target[pathParts[i]]) {
          target[pathParts[i]] = {};
        }
        target[pathParts[i]] = { ...target[pathParts[i]] };
        target = target[pathParts[i]];
      }

      target[pathParts[pathParts.length - 1]] = mapping;

      updateSettingBlueprint("fieldMapping", newFieldMapping);
    },
    [blueprint, updateSettingBlueprint]
  );

  const getFieldsCategoryList = useCallback((type, category) => {
    switch (category) {
      case "work-order":
        if (type === "required") {
          return workOrderRequiredFields;
        } else if (type === "optional") {
          return workOrderOptionalFields;
        }
        break;
      case "product":
        if (type === "required") {
          return productRequiredFields;
        } else if (type === "optional") {
          return productOptionalFields;
        }
        break;
      case "work-order-op-history":
        if (type === "required") {
          return workOrderOpHistoryRequiredFields;
        } else if (type === "optional") {
          return workOrderOpHistoryOptionalFields;
        }
        break;
      case "route":
        if (type === "required") {
          return routeRequiredFields;
        } else if (type === "optional") {
          return routeOptionalFields;
        }
        break;
      case "operation":
        if (type === "required") {
          return opRequiredFields;
        } else if (type === "optional") {
          return opOptionalFields;
        }
        break;
      case "equipment":
        if (type === "required") {
          return equipmentRequiredFields;
        }
        break;
      case "worker":
        if (type === "required") {
          return workerRequiredFields;
        } else if (type === "optional") {
          return workerOptionalFields;
        }
        break;
      default:
        return [];
    }
  }, [
    equipmentRequiredFields,
    opOptionalFields,
    opRequiredFields,
    productOptionalFields,
    productRequiredFields,
    routeOptionalFields,
    routeRequiredFields,
    workOrderOpHistoryOptionalFields,
    workOrderOpHistoryRequiredFields,
    workOrderOptionalFields,
    workOrderRequiredFields,
    workerOptionalFields,
    workerRequiredFields,
  ]);

  const renderFields = useCallback((type, category) => {
    let categoryValues = getFieldsCategoryList(type, category);

    return categoryValues?.map((field) => {
      const fp = `${type}.${category}.${field.key}`;
      const pathParts = fp.split(".");
      let currentMapping = blueprint["fieldMapping"];
      for (const part of pathParts) {
        currentMapping = currentMapping?.[part];
      }
      return (
        <FieldMappingRow
          key={fp}
          disabled={approval}
          connectionType={connectionType}
          joinForm={joinForm}
          filterForm={filterForm}
          transformForm={transformForm}
          field={field}
          fieldPath={fp}
          currentMapping={currentMapping}
          onMappingChange={updateFieldMapping}
        />
      );
    });
  }, [
    approval,
    blueprint,
    connectionType,
    filterForm,
    joinForm,
    transformForm,
    updateFieldMapping,
    getFieldsCategoryList
  ]);

  const fieldHeader = (
    <Row gutter={16} style={{ marginBottom: 16, fontWeight: "bold" }}>
      <Col span={4}>SFR 欄位</Col>
      {connectionType === "database" ? (
        <>
          <Col span={5}>ERP 資料表</Col>
          <Col span={5}>ERP 欄位</Col>
          <Col span={3}>JOIN 條件</Col>
          <Col span={3}>篩選條件</Col>
          <Col span={3}>轉換設定</Col>
        </>
      ) : (
        <Col span={18}>對應的欄位</Col>
      )}
      <Col span={1}>備註</Col>
    </Row>
  );

  const workOrderItems = (type) => [
    {
      key: "work-order",
      label: "大工單相關",
      children: (
        <>
          {fieldHeader}
          <Divider style={{ margin: "10px 0" }} />
          {renderFields(type, "work-order")}
          <br />
          <Collapse
            defaultActiveKey={["product"]}
            destroyOnHidden={true}
            items={[
              {
                key: "product",
                label: "物料相關",
                children: (
                  <>
                    {fieldHeader}
                    <Divider style={{ margin: "10px 0" }} />
                    {renderFields(type, "product")}
                  </>
                ),
              },
            ]}
          />
        </>
      ),
    },
  ];

  const workOrderOpHistoryItems = (type) => [
    {
      key: "work-order-op-history",
      label: "小工單相關",
      children: (
        <>
          {fieldHeader}
          <Divider style={{ margin: "10px 0" }} />
          {renderFields(type, "work-order-op-history")}
          <br />
          <Collapse
            defaultActiveKey={["route", "operation", "equipment", "worker"]}
            destroyOnHidden={true}
            items={[
              {
                key: "route",
                label: "途程相關",
                children: (
                  <>
                    {fieldHeader}
                    <Divider style={{ margin: "10px 0" }} />
                    {renderFields(type, "route")}
                  </>
                ),
              },
              {
                key: "operation",
                label: "工序相關",
                children: (
                  <>
                    {fieldHeader}
                    <Divider style={{ margin: "10px 0" }} />
                    {renderFields(type, "operation")}
                  </>
                ),
              },
              {
                key: "equipment",
                label: "設備相關",
                children: (
                  <>
                    {fieldHeader}
                    <Divider style={{ margin: "10px 0" }} />
                    {renderFields(type, "equipment")}
                  </>
                ),
              },
              {
                key: "worker",
                label: "員工相關",
                children: (
                  <>
                    {fieldHeader}
                    <Divider style={{ margin: "10px 0" }} />
                    {renderFields(type, "worker")}
                  </>
                ),
              },
            ].filter(item => {
              const list = getFieldsCategoryList(type, item.key);
              return list && list.length > 0;
            })}
          />
        </>
      ),
    },
  ];

  return (
    <div>
      <FieldMappingStepDrawer open={drawerOpen} setOpen={setDrawerOpen} />
      <Title level={4}>
        欄位映射配置
        <Button
          type="text"
          shape="circle"
          icon={<QuestionCircleTwoTone />}
          onClick={() => setDrawerOpen(true)}
        />
      </Title>
      <Text type="secondary">
        配置 ERP 系統欄位與 SFR 系統欄位的對應關係，包括必填欄位和工單結構映射。
      </Text>

      <Tabs
        defaultActiveKey="required"
        style={{ marginTop: 24 }}
        items={[
          {
            key: "required",
            label: <div style={{ padding: '0px 8px' }}> 必填欄位 </div>,
            children: (
              <>
                <Collapse
                  defaultActiveKey={["work-order"]}
                  items={workOrderItems("required")}
                />
                <br />
                <Collapse
                  defaultActiveKey={["work-order-op-history"]}
                  items={workOrderOpHistoryItems("required")}
                />
              </>
            ),
          },
          {
            key: "optional",
            forceRender: true,
            label: <div style={{ padding: '0px 8px' }}> 選填欄位 </div>,
            children: (
              <>
                <Collapse
                  defaultActiveKey={["work-order"]}
                  items={workOrderItems("optional")}
                />
                <br />
                <Collapse
                  defaultActiveKey={["work-order-op-history"]}
                  items={workOrderOpHistoryItems("optional")}
                />
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default FieldMappingStep;
